import React, { useState } from "react";
import Layout from "../containers/layout";
import {
  BlockStack,
  Box,
  Button,
  Card,
  Page,
  Select,
  Text,
  TextField,
  Toast,
} from "@shopify/polaris";

const OrderUpdate = () => {
  const [orderNumber, setOrderNumber] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("Confirmed");
  const [selectedStore, setSelectedStore] = useState("iPlanet");
  const [selectedFulfillment, setSelectedFulfillment] = useState("");
  const [fulfillmentOptions, setFulfillmentOptions] = useState([]);
  const [showFulfillmentFields, setShowFulfillmentFields] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const ORDER_PREFIX = "gid://shopify/Order/";

  const fullOrderId = orderNumber.trim()
    ? `${ORDER_PREFIX}${orderNumber.trim()}`
    : "";

  const storeOptions = [
    { label: "iPlanet", value: "iPlanet" },
    { label: "Nexus", value: "Nexus" },
    { label: "Indigo", value: "Indigo" },
  ];

  const statusOptions = [
    { label: "Confirmed", value: "Confirmed" },
    { label: "In Transit", value: "IN Transit" },
    { label: "Out for Delivery", value: "Out For Delivery" },
    { label: "Delivered", value: "Delivered" },
    { label: "Failed", value: "failure" },
    { label: "Attempted Delivery", value: "Attempted_Delivery" },
  ];

  const statusMap = {
    Confirmed: "confirmed",
    "IN Transit": "in_transit",
    "Out For Delivery": "out_for_delivery",
    Delivered: "delivered",
    failure: "failure",
    Attempted_Delivery: "attempted_delivery",
  };

  const getBaseUrl = () => "https://api.iplanet.one";

  const getGetFulfillmentUrl = () => {
    if (selectedStore === "iPlanet") {
      return `${getBaseUrl()}/orderevent/getFulfillmentIds`;
    }
    return `${getBaseUrl()}/${selectedStore.toLowerCase()}/orderevent/getFulfillmentIds`;
  };

  const getUpdateFulfillmentUrl = () => {
    if (selectedStore === "iPlanet") {
      return `${getBaseUrl()}/orderevent/updateOrderStatus`;
    }
    return `${getBaseUrl()}/${selectedStore.toLowerCase()}/orderevent/updateOrderStatus`;
  };

  const showToast = (tone, message) => {
    setToast({ tone, message });
    setTimeout(() => setToast(null), 5000);
  };

  const handleGetFulfillment = async () => {
    if (!orderNumber.trim()) {
      showToast("critical", "Order ID is required");

      return;
    }

    setLoading(true);
    setShowFulfillmentFields(false);
    setFulfillmentOptions([]);

    try {
      const url = getGetFulfillmentUrl();
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderId: fullOrderId }),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      const ids = data.fulfillmentIds || data?.fulfillments || data || [];

      if (ids.length === 0) {
        showToast(
          "warning",
          "No Fulfillment IDs",
          "No fulfillment IDs found for this order",
        );
      } else {
        const options = ids.map((id) => ({ label: id, value: id }));
        setFulfillmentOptions(options);
        setSelectedFulfillment(ids[0] || "");
        setShowFulfillmentFields(true);
        showToast(
          "success",
          "Success",
          `Found ${ids.length} fulfillment ID(s)`,
        );
      }
    } catch (err) {
      console.error(err);
      showToast(
        "critical",
        "Error",
        err.message || "Failed to fetch fulfillment IDs",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!orderNumber.trim() || !selectedFulfillment) {
      showToast(
        "critical",
        "Error",
        "Order ID and Fulfillment ID are required",
      );
      return;
    }

    setLoading(true);

    try {
      const url = getUpdateFulfillmentUrl();
      const apiStatus =
        statusMap[selectedStatus] || selectedStatus.toLowerCase();

      const payload = {
        fulfillmentIds: [selectedFulfillment],
        status: apiStatus,
        message: `Order updated to ${selectedStatus} via Wondersoft (Fulfillment: ${selectedFulfillment})`,
      };

      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Update failed: ${response.status}`,
        );
      }

      showToast(
        "success",
        `Fulfillment ${selectedFulfillment} updated to ${selectedStatus} successfully!`,
      );
      setOrderNumber("");
      setSelectedStatus("Confirmed");
      setSelectedStore("iPlanet");
      setSelectedFulfillment("");
      setFulfillmentOptions([]);
      setShowFulfillmentFields(false);
    } catch (err) {
      console.error("Update error:", err);
      showToast(
        "critical",
        "Update Failed",
        err.message || "Something went wrong",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Page fullWidth>
        <BlockStack gap="600">
          <Card padding="600">
            <BlockStack gap="500">
              <BlockStack gap="200" align="center">
                <Text variant="heading2xl" as="h1" alignment="center">
                  Order Status Update
                </Text>
                <Text variant="bodyMd" tone="subdued" alignment="center">
                  Update order status from Wondersoft
                </Text>
              </BlockStack>

              <BlockStack gap="400">
                {toast && (
                  <Toast
                    content={toast.message}
                    tone={toast.tone}
                    onDismiss={() => setToast(null)}
                  />
                )}

                <Card background="bg-surface-secondary" rounded="300">
                  <Box padding="400">
                    <BlockStack gap="400">
                      <Box
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "1.5rem",
                          alignItems: "flex-end",
                          justifyContent: "center",
                        }}
                      >
                        <TextField
                          label="Order ID"
                          placeholder="Enter Order ID"
                          value={orderNumber}
                          onChange={setOrderNumber}
                          disabled={loading || showFulfillmentFields}
                        />

                        <Select
                          label="Store"
                          options={storeOptions}
                          onChange={(value) => {
                            setSelectedStore(value);
                            setShowFulfillmentFields(false);
                            setFulfillmentOptions([]);
                            setSelectedFulfillment("");
                          }}
                          value={selectedStore}
                          disabled={loading || showFulfillmentFields}
                        />

                        {showFulfillmentFields &&
                          fulfillmentOptions.length > 0 && (
                            <>
                              <Select
                                label="Fulfillment ID"
                                options={fulfillmentOptions}
                                onChange={setSelectedFulfillment}
                                value={selectedFulfillment}
                                disabled={loading}
                              />

                              <Select
                                label="Status"
                                options={statusOptions}
                                onChange={setSelectedStatus}
                                value={selectedStatus}
                                disabled={loading}
                              />
                            </>
                          )}

                        {!loading && !showFulfillmentFields && (
                          <Button
                            variant="primary"
                            size="large"
                            onClick={handleGetFulfillment}
                            loading={loading}
                          >
                            Get Fulfillment ID
                          </Button>
                        )}

                        {showFulfillmentFields && (
                          <Button
                            variant="primary"
                            size="large"
                            onClick={handleUpdate}
                            loading={loading}
                          >
                            Update Order Status
                          </Button>
                        )}
                      </Box>
                    </BlockStack>
                  </Box>
                </Card>
              </BlockStack>
            </BlockStack>
          </Card>
        </BlockStack>
      </Page>
    </Layout>
  );
};

export default OrderUpdate;
